import { Injectable } from '@angular/core';
import { GRID_LEVEL_COL, IColumnExportingEventArgs, IgxColumnComponent, IgxExcelExporterOptions } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { Utilities } from "@tylertech/shared-module";

export type ExportFormatter = (value: any, rowData?: any) => any;

export interface IExportColumnConfiguration
{
    /** the always hidden column igx writes its group captions into, undefined when there is none */
    groupCaptionColumn: IgxColumnComponent;
    /** header the caption column has to be exported under */
    captionHeader: string;
    /** fields of the hidden columns that have to be cancelled in columnExporting */
    hiddenColumnFields: string[];
    /** keeps igx's internal level column out of the file, true when it handled the column */
    handleLevelColumn(columnArgs: IColumnExportingEventArgs): boolean;
    /** puts back every formatter this configuration replaced, safe to call more than once */
    restoreFormatters(): void;
}

@Injectable()
export class GridExportService
{
    configureExportColumns(grid: IgxGridComponent, selectedRowIds: Set<any>): IExportColumnConfiguration
    {
        this.repairDerivedColumnWidths(grid);

        const isGrouped = !Utilities.IsNullOrUndefinedOrEmpty(grid.groupingExpressions);
        const primaryKey = grid.primaryKey;
        const hasSelection = selectedRowIds.size > 0;

        const firstExportedColumn = isGrouped ? grid.pinnedColumns[0] ?? grid.columns[0] : undefined;
        const groupCaptionColumn = firstExportedColumn?.hidden ? firstExportedColumn as IgxColumnComponent : undefined;
        // the Selected column keeps its own header when it doubles as the caption column
        const captionHeader = groupCaptionColumn ?
            (hasSelection && groupCaptionColumn.field === "_selected" ? groupCaptionColumn.header : "Group") :
            undefined;

        const hiddenColumnFields = grid.columns
            .filter(c => c.hidden && (c.field !== "_selected" || !hasSelection) && c !== groupCaptionColumn)
            .map(c => c.field);

        const originalFormatters = new Map<IgxColumnComponent, ExportFormatter>();
        const setExportFormatter = (column: IgxColumnComponent, formatter: ExportFormatter): void =>
        {
            if (!column || originalFormatters.has(column))
                return;
            originalFormatters.set(column, column.formatter);
            column.formatter = formatter;
        };

        if (hasSelection)
        {
            setExportFormatter(grid.columns.find(c => c.field === "_selected") as IgxColumnComponent, (value, rowData) =>
                Utilities.IsNullOrUndefined(rowData?.[primaryKey]) ? "" : selectedRowIds.has(rowData[primaryKey]) ? "Yes" : "No");
        }

        setExportFormatter(groupCaptionColumn, () => "");

        return {
            groupCaptionColumn,
            captionHeader,
            hiddenColumnFields,
            handleLevelColumn: (columnArgs: IColumnExportingEventArgs): boolean =>
            {
                if (columnArgs.field !== GRID_LEVEL_COL)
                    return false;
                if (isGrouped)
                    columnArgs.header = "";
                else
                    columnArgs.cancel = true;
                return true;
            },
            restoreFormatters: (): void =>
            {
                originalFormatters.forEach((formatter, column) => column.formatter = formatter);
                originalFormatters.clear();
            }
        };
    }

    createExcelExportOptions(title: string, isGrouped: boolean): IgxExcelExporterOptions
    {
        const options = new IgxExcelExporterOptions(title);
        // Excel will not expand or collapse outline rows that sit inside a table, which leaves the
        // exported groups stuck in the state they were exported in
        options.exportAsTable = !isGrouped;
        return options;
    }

    private repairDerivedColumnWidths(grid: IgxGridComponent): void
    {
        const fallbackWidth = `${Number.isFinite(grid.minColumnWidth) ? grid.minColumnWidth : 80}px`;

        grid.columns.forEach(column =>
        {
            //allowed width, continue
            if (!column.width || Number.isFinite(Number.parseFloat(column.width)))
                return;

            if (column.widthSetByUser)
                column.width = fallbackWidth;
            else
                column.defaultWidth = fallbackWidth;
        });
    }
}
